import { MoneyInputDirective } from '../../shared/ui/money-input.directive';
import { Component, inject, OnInit, OnDestroy, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { GameService } from '../../core/state/game.service';
import {
  LedgerEntry,
  StadiumCatalogUpgradeOption,
  StadiumUpgradeQuoteResponse,
  StadiumUpgradeModule,
  UpgradeOperation,
  StadiumUpgradeContractRequest,
  SponsorMarketOffer,
  SponsorContract,
  SponsorType,
} from '../../core/models/types';
import { estimateStadiumAttendance, normalizeTicketPrice } from './attendance-estimate';
import { stadiumUpgradesApi } from '../../core/http/stadium-upgrades.api';
import { IconComponent } from '../../shared/ui/icon/icon.component';
import { MatchdayLoansComponent } from './components/matchday-loans/matchday-loans.component';

import { StaffPanelComponent } from './staff-panel.component';
import { StadiumNameComponent } from './stadium-name.component';
import { groupDailyAwards, isSportingAward } from './ledger-awards';

import { ClubShieldComponent } from '../../shared/ui/club-shield/club-shield.component';

export type ClubMainTab = 'stadium' | 'staff' | 'economy' | 'sponsors';

@Component({
  selector: 'app-stadium-economy',
  standalone: true,
  imports: [MoneyInputDirective, CommonModule, FormsModule, IconComponent, MatchdayLoansComponent, StadiumNameComponent, StaffPanelComponent, ClubShieldComponent],
  templateUrl: './stadium-economy.component.html',
  styleUrl: './stadium-economy.component.scss',
})
export class StadiumEconomyComponent implements OnInit, OnDestroy {
  readonly game = inject(GameService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  activeTab: ClubMainTab = 'stadium';
  ticketPriceInput = this.game.userClub().stadium.ticketPrice;
  loanAmount = 10.0;
  loanYears = 2;

  // Remodelaciones de estadio: Estado de UI
  readonly activeQuote = signal<StadiumUpgradeQuoteResponse | null>(null);
  readonly isQuoting = signal<boolean>(false);
  readonly isSubmittingUpgrade = signal<boolean>(false);
  readonly isCancellingUpgrade = signal<boolean>(false);
  readonly isUpdatingTicketPrice = signal<boolean>(false);
  readonly quoteError = signal<string | null>(null);
  readonly lastConflictMessage = signal<string | null>(null);

  // Almacenar último payload idempotente para reintento en error 503 / timeout
  private lastContractPayload: {
    clubId: string;
    body: StadiumUpgradeContractRequest;
  } | null = null;
  readonly canRetryLastContract = signal<boolean>(false);

  // Paginación del registro contable (ledger)
  readonly ledgerPage = signal<number>(0);
  readonly isLoadingMoreLedger = signal<boolean>(false);
  readonly hasMoreLedger = signal<boolean>(true);

  async loadMoreLedger(): Promise<void> {
    if (this.isLoadingMoreLedger() || !this.hasMoreLedger()) return;
    const club = this.game.userClub();
    const clubId = club?.id || 'club-user';
    const nextPage = this.ledgerPage() + 1;
    this.isLoadingMoreLedger.set(true);
    try {
      const res = await this.game.fetchClubLedger(clubId, nextPage, 20);
      this.ledgerPage.set(nextPage);
      if (res) {
        const obj = res as any;
        const rawItems = obj.content || obj.items || obj.data || obj.entries || obj.transactions || obj.ledger || [];
        if (rawItems.length === 0 || (obj.totalPages != null && nextPage >= obj.totalPages - 1)) {
          this.hasMoreLedger.set(false);
        }
      } else {
        this.hasMoreLedger.set(false);
      }
    } catch (e) {
      console.warn('Error loading more ledger pages:', e);
    } finally {
      this.isLoadingMoreLedger.set(false);
    }
  }


  // Módulos comerciales e instalaciones (PARKING, CAFETERIA, MERCHANDISING, BATHROOMS)
  readonly facilityModules: StadiumUpgradeModule[] = [
    'PARKING',
    'CAFETERIA',
    'MERCHANDISING',
    'BATHROOMS',
  ];

  get ledgerList(): LedgerEntry[] {
    const club = this.game.userClub();
    const clubLedger = club?.economy?.ledger || [];

    const rawData = this.game.ledgerData();
    if (rawData) {
      let rawItems: any[] = [];
      if (Array.isArray(rawData)) {
        rawItems = rawData;
      } else if (typeof rawData === 'object') {
        const obj = rawData as any;
        rawItems =
          obj.content ||
          obj.items ||
          obj.data ||
          obj.entries ||
          obj.transactions ||
          obj.ledger ||
          obj.results ||
          [];
      }
      const mapped = rawItems.map((item: any, idx: number) => {
        const rawAmount = typeof item.amount === 'number' ? item.amount : Number(item.amount || 0);
        const award = isSportingAward(item.category || item.conceptCategory);
        const amountM = award ? rawAmount / 1_000_000
          : rawAmount >= 10_000 ? +(rawAmount / 1_000_000).toFixed(3) : +rawAmount.toFixed(3);
        const direction = String(item.direction || item.type || '').toUpperCase();
        const isCredit =
          direction === 'CREDIT' ||
          direction === 'INGRESO' ||
          direction === 'INCOME' ||
          direction === 'DEPOSIT';

        return {
          id: String(
            item.id ||
            item.entryId ||
            item.transactionId ||
            item.reference ||
            item.sourceReference ||
            `led-${idx}-${Date.now()}`
          ),
          timestamp:
            (award ? item.recordedAt : null) ||
            item.occurredAt ||
            item.recordedAt ||
            item.createdAt ||
            item.timestamp ||
            item.date ||
            new Date().toISOString(),
          type: (isCredit ? 'INGRESO' : 'GASTO') as 'INGRESO' | 'GASTO',
          amount: Math.abs(amountM),
          concept:
            item.concept ||
            item.description ||
            item.title ||
            item.category ||
            'Movimiento contable',
          runningBalance: item.balanceAfter != null && Number.isFinite(Number(item.balanceAfter))
            ? Number(item.balanceAfter) / 1_000_000
            : null,
          direction: isCredit ? 'CREDIT' : 'DEBIT',
          category: item.category || item.conceptCategory || '',
          sourceType: item.sourceType || item.source || item.origin || 'CLUB',
          sourceReference: item.sourceReference || item.reference || '',
          occurredAt: item.occurredAt || item.recordedAt,
        };
      });

      return groupDailyAwards(mapped);
    }

    return groupDailyAwards(clubLedger);
  }

  get ledgerSummary(): {
    initialBudget: number;
    totalIncome: number;
    totalExpenses: number;
    currentBalance: number;
    netChange: number;
  } {
    const list = this.ledgerList;
    let initialBudget = 100.0;
    let totalIncome = 0;
    let totalExpenses = 0;

    for (const e of list) {
      const isCredit = e.direction === 'CREDIT' || e.type === 'INGRESO';
      const isInitial = e.category === 'INITIAL_BUDGET' || (e.concept && e.concept.toLowerCase().includes('initial'));
      if (isInitial) {
        initialBudget = e.amount;
      } else if (isCredit) {
        totalIncome += e.amount;
      } else {
        totalExpenses += e.amount;
      }
    }

    const currentBalance = +(initialBudget + totalIncome - totalExpenses).toFixed(2);
    const netChange = +(totalIncome - totalExpenses).toFixed(2);

    return {
      initialBudget: +initialBudget.toFixed(2),
      totalIncome: +totalIncome.toFixed(2),
      totalExpenses: +totalExpenses.toFixed(2),
      currentBalance,
      netChange,
    };
  }

  private lastSyncedTab: ClubMainTab | null = null;

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const tab = (params['tab'] as ClubMainTab) || 'stadium';
      const validTab = ['stadium', 'staff', 'economy', 'sponsors'].includes(tab) ? tab : 'stadium';
      if (this.activeTab !== validTab || this.lastSyncedTab !== validTab) {
        this.activeTab = validTab;
        this.lastSyncedTab = validTab;
        this.syncTabData(false);
      }
    });
  }

  ngOnDestroy(): void {}

  setTab(tab: ClubMainTab): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
    });
  }

  async syncTabData(forceRefresh: boolean = false): Promise<void> {
    const club = this.game.userClub();
    const clubId = club?.id || 'club-user';

    if (this.activeTab === 'stadium') {
      const [stadium, ticketPriceRes] = await Promise.all([
        this.game.fetchClubStadium(clubId, forceRefresh),
        this.game.fetchClubTicketPrice(clubId, forceRefresh),
        this.game.fetchStadiumCatalog(clubId, forceRefresh),
        this.game.fetchActiveConstruction(clubId, forceRefresh),
      ]);
      if (ticketPriceRes && typeof ticketPriceRes.ticketPrice === 'number') {
        this.ticketPriceInput = ticketPriceRes.ticketPrice;
      }

    } else if (this.activeTab === 'economy') {
      if (forceRefresh) {
        this.ledgerPage.set(0);
        this.hasMoreLedger.set(true);
      }
      await Promise.all([
        this.game.fetchClubEconomy(clubId, forceRefresh),
        this.game.fetchMatchdayLoanOffer(clubId, forceRefresh),
        this.game.fetchAggregatedDebt(clubId, forceRefresh),
        this.game.fetchEconomicSummary(clubId, forceRefresh),
      ]);
    } else if (this.activeTab === 'sponsors') {
      await Promise.all([
        this.game.fetchSponsorContracts(clubId, forceRefresh),
        this.game.fetchSponsorOffers(clubId, forceRefresh),
      ]);
    }
  }

  get isCurrentTabLoading(): boolean {
    if (this.activeTab === 'stadium') {
      return (
        this.game.isStadiumLoading() ||
        this.game.isCatalogLoading() ||
        this.game.isConstructionLoading()
      );
    }
    if (this.activeTab === 'staff') return this.game.isStaffLoading();
    if (this.activeTab === 'economy') return this.game.isEconomyLoading();
    if (this.activeTab === 'sponsors') {
      return (
        this.game.isSponsorOffersLoading() ||
        this.game.isSponsorContractsLoading()
      );
    }
    return false;
  }


  // --- HELPERS DE ESTADIO & CATÁLOGO ---
  get activeConstructionOp(): UpgradeOperation | null {
    const op = this.game.constructionData()?.operation;
    if (!op) return null;
    // Una obra solo está "en curso" si existe y no ha finalizado
    if (['APPLIED', 'FAILED', 'CANCELLED'].includes(op.status)) {
      return null;
    }
    return op;
  }

  get effectiveCapacity(): number {
    return this.game.stadiumData()?.capacity ?? this.game.userClub().stadium.capacity;
  }

  get maximumCapacity(): number {
    return this.game.catalogData()?.maximumCapacity ?? 110000;
  }

  get standsUpgradePacks(): StadiumCatalogUpgradeOption[] {
    const catalog = this.game.catalogData();
    if (!catalog || !catalog.upgrades) return [];
    return catalog.upgrades.filter((u) => u.module === 'STANDS');
  }

  isStandsPackDisabled(pack: StadiumCatalogUpgradeOption): boolean {
    const currentCap = this.effectiveCapacity;
    const maxCap = this.maximumCapacity;
    const increase = pack.capacityIncrease || 0;
    return currentCap + increase > maxCap;
  }

  getFacilityNextUpgrade(module: StadiumUpgradeModule): StadiumCatalogUpgradeOption | null {
    const currentLevel = this.getModuleCurrentLevel(module);
    const catalog = this.game.catalogData();
    if (!catalog || !catalog.upgrades) return null;
    const target = currentLevel + 1;
    return catalog.upgrades.find((u) => u.module === module && u.targetLevel === target) || null;
  }

  getModuleCurrentLevel(module: StadiumUpgradeModule): number {
    const stadium = this.game.stadiumData();
    if (!stadium) return 1;
    switch (module) {
      case 'PARKING':
        return stadium.parkingLevel ?? 1;
      case 'CAFETERIA':
        return stadium.cafeteriaLevel ?? 1;
      case 'MERCHANDISING':
        return stadium.merchandisingLevel ?? 1;
      case 'BATHROOMS':
        return stadium.bathroomLevel ?? 1;
      case 'STANDS':
        return stadium.standsLevel ?? 0;
      default:
        return 1;
    }
  }

  getModuleTitle(module: StadiumUpgradeModule): string {
    switch (module) {
      case 'STANDS':
        return 'Gradas y Aforo';
      case 'PARKING':
        return 'Parking Estadio';
      case 'CAFETERIA':
        return 'Cafeterías & Hostelería';
      case 'MERCHANDISING':
        return 'Tiendas Oficiales';
      case 'BATHROOMS':
        return 'Aseos y Sanitarios';
      default:
        return module;
    }
  }

  getModuleDescription(module: StadiumUpgradeModule): string {
    switch (module) {
      case 'STANDS':
        return 'Aumenta el aforo del estadio para albergar más espectadores y generar más ingresos.';
      case 'PARKING':
        return 'Mejora el acceso, la comodidad y el gasto medio por vehículo en día de partido.';
      case 'CAFETERIA':
        return 'Incrementa el consumo de comida y bebidas durante las previas y descansos.';
      case 'MERCHANDISING':
        return 'Venta de camisetas, bufandas y productos oficiales del club en el estadio.';
      case 'BATHROOMS':
        return 'Moderniza la higiene y el confort general de los espectadores en todas las gradas.';
      default:
        return '';
    }
  }

  getOperationStatusLabel(status?: string): string {
    switch (status) {
      case 'REQUESTED':
        return 'Solicitud pendiente de financiación';
      case 'FUNDS_CONFIRMED':
      case 'FUNDS_CAPTURED':
        return 'Obra en curso';
      case 'CAPTURE_PENDING':
        return 'Finalizando la mejora';
      case 'CANCEL_REQUESTED':
        return 'Cancelación en curso';
      case 'REVERSAL_PENDING':
        return 'Reversión económica en curso';
      case 'APPLIED':
        return 'Mejora aplicada';
      case 'FAILED':
        return 'Solicitud fallida';
      case 'CANCELLED':
        return 'Solicitud cancelada';
      default:
        return status || 'Estado desconocido';
    }
  }

  getOperationStatusBadgeClass(status?: string): string {
    switch (status) {
      case 'REQUESTED':
      case 'FUNDS_CONFIRMED':
      case 'FUNDS_CAPTURED':
        return 'bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-700';
      case 'CAPTURE_PENDING':
        return 'bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-950/60 dark:text-blue-300 dark:border-blue-700';
      case 'CANCEL_REQUESTED':
      case 'REVERSAL_PENDING':
        return 'bg-purple-50 text-purple-700 border-purple-300 dark:bg-purple-950/60 dark:text-purple-300 dark:border-purple-700';
      case 'APPLIED':
        return 'bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-700';
      case 'FAILED':
      case 'CANCELLED':
        return 'bg-rose-50 text-rose-700 border-rose-300 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-700';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';
    }
  }

  canCancelOperation(op?: UpgradeOperation | null): boolean {
    if (!op) return false;
    return op.status === 'REQUESTED' || op.status === 'FUNDS_CONFIRMED';
  }

  // --- PRESUPUESTAR (QUOTE) ---
  async requestQuote(module: StadiumUpgradeModule, upgradeCode?: string): Promise<void> {
    const clubId = this.game.userClub()?.id;
    if (!clubId) return;

    this.isQuoting.set(true);
    this.quoteError.set(null);
    this.lastConflictMessage.set(null);
    this.canRetryLastContract.set(false);
    this.lastContractPayload = null;

    try {
      const quote = await stadiumUpgradesApi.getQuote(clubId, module, upgradeCode);
      this.activeQuote.set(quote);
    } catch (err: any) {
      console.warn('Error solicitando presupuesto de remodelación:', err);
      const msg = this.formatErrorMessage(err);
      this.game.showNotification(msg, 'error');
      if (err?.status === 409) {
        await this.syncTabData(true);
      }
    } finally {
      this.isQuoting.set(false);
    }
  }

  closeQuoteModal(): void {
    if (this.isSubmittingUpgrade()) return;
    this.activeQuote.set(null);
    this.quoteError.set(null);
  }

  // --- CONTRATAR (POST /api/clubs/{clubId}/stadium/upgrades) ---
  async confirmAndContractUpgrade(): Promise<void> {
    const quote = this.activeQuote();
    const clubId = this.game.userClub()?.id;
    if (!quote || !clubId || this.isSubmittingUpgrade()) return;

    // Generar un UUID único para la operación
    const operationId = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : 'op-' + Math.random().toString(36).substring(2, 9) + '-' + Date.now();

    const body: StadiumUpgradeContractRequest = {
      operationId,
      module: quote.module,
      upgradeCode: quote.upgradeCode,
      configurationToken: quote.configurationToken,
      ruleSetVersion: quote.ruleSetVersion,
      quotedCost: quote.quotedCost,
      expectedStadiumVersion: quote.stadiumVersion,
    };

    // Almacenar para reintento idempotente con el mismo body y operationId ante 503 o fallo de red
    this.lastContractPayload = { clubId, body };
    this.canRetryLastContract.set(false);
    this.quoteError.set(null);
    this.isSubmittingUpgrade.set(true);

    await this.executeContractRequest(clubId, body);
  }

  // Reintento idempotente conservando exactamente el mismo operationId y body
  async retryLastContractRequest(): Promise<void> {
    if (!this.lastContractPayload || this.isSubmittingUpgrade()) return;
    this.quoteError.set(null);
    this.canRetryLastContract.set(false);
    this.isSubmittingUpgrade.set(true);

    await this.executeContractRequest(
      this.lastContractPayload.clubId,
      this.lastContractPayload.body
    );
  }

  private async executeContractRequest(
    clubId: string,
    body: StadiumUpgradeContractRequest
  ): Promise<void> {
    try {
      const createdOp = await stadiumUpgradesApi.requestUpgrade(clubId, body);
      this.game.showNotification(
        'Solicitud de remodelación aceptada. Se procesará al cierre de jornada.',
        'success'
      );
      this.activeQuote.set(null);
      this.lastContractPayload = null;
      this.canRetryLastContract.set(false);

      // Refrescar obras y saldo mediante los mecanismos existentes
      await Promise.all([
        this.game.fetchActiveConstruction(clubId, true),
        this.game.fetchClubBalance(clubId, true),
        this.game.fetchClubStadium(clubId, true),
      ]);
    } catch (err: any) {
      console.warn('Error contratando remodelación:', err);
      const status = err?.status;
      const msg = this.formatErrorMessage(err);

      if (status === 409) {
        // En 409: refrescar estadio, obra y presupuesto y explicar el conflicto sin reintentar automáticamente con otro UUID
        this.lastConflictMessage.set(msg);
        this.quoteError.set(msg);
        this.canRetryLastContract.set(false);
        this.lastContractPayload = null;
        await Promise.all([
          this.game.fetchClubStadium(clubId, true),
          this.game.fetchActiveConstruction(clubId, true),
          this.game.fetchStadiumCatalog(clubId, true),
          this.game.fetchClubBalance(clubId, true),
        ]);
      } else if (status === 503 || !status) {
        // En 503 o fallo de red: dependencia temporalmente no disponible, permitir reintento conservando identidad
        this.quoteError.set(`Servicio no disponible temporalmente. Puedes reintentar la solicitud.`);
        this.canRetryLastContract.set(true);
      } else {
        this.quoteError.set(msg);
        this.canRetryLastContract.set(false);
      }
    } finally {
      this.isSubmittingUpgrade.set(false);
    }
  }

  // --- CANCELAR OBRA (PUT /api/clubs/{clubId}/stadium/upgrades/{operationId}/cancellation) ---
  async cancelActiveUpgrade(): Promise<void> {
    const currentConstruction = this.game.constructionData();
    const op = currentConstruction?.operation;
    const clubId = this.game.userClub()?.id;
    if (!op || !clubId || this.isCancellingUpgrade()) return;

    if (!this.canCancelOperation(op)) {
      this.game.showNotification('Esta obra ya no puede ser cancelada en su estado actual.', 'error');
      return;
    }

    const confirmCancel = window.confirm(this.game.t('stadium.cancel_confirm_msg'));
    if (!confirmCancel) return;

    this.isCancellingUpgrade.set(true);
    try {
      // Envía expectedVersion: operation.version (versión de la operación, NO stadiumVersion)
      await stadiumUpgradesApi.cancelUpgrade(clubId, op.id, op.version);
      this.game.showNotification('Cancelación solicitada con éxito. Procesando...', 'info');

      // Continuar consultando hasta estado terminal
      await Promise.all([
        this.game.fetchActiveConstruction(clubId, true),
        this.game.fetchClubBalance(clubId, true),
      ]);
    } catch (err: any) {
      console.warn('Error cancelando obra de remodelación:', err);
      const msg = this.formatErrorMessage(err);
      this.game.showNotification(msg, 'error');
      if (err?.status === 409) {
        await Promise.all([
          this.game.fetchActiveConstruction(clubId, true),
          this.game.fetchClubStadium(clubId, true),
          this.game.fetchClubBalance(clubId, true),
        ]);
      }
    } finally {
      this.isCancellingUpgrade.set(false);
    }
  }

  private formatErrorMessage(err: any): string {
    const code = err?.code || err?.responseBody?.code || err?.failure?.code;
    const message = err?.responseBody?.message || err?.failure?.message || err?.message;

    if (code === 'ACTIVE_CONSTRUCTION_EXISTS') {
      return 'Ya existe una obra activa en el estadio. Debe completarse o cancelarse antes.';
    }
    if (code === 'INSUFFICIENT_FUNDS') {
      return 'Fondos insuficientes en la tesorería del club para afrontar el presupuesto.';
    }
    if (code === 'MAXIMUM_CAPACITY_EXCEEDED') {
      return 'El pack de gradas seleccionado supera el aforo máximo permitido del estadio.';
    }
    if (code === 'MAXIMUM_LEVEL_REACHED') {
      return 'La instalación ya ha alcanzado su nivel máximo permitido.';
    }
    if (code === 'MATCHDAY_SLOT_CONSUMED') {
      return 'Solo se admite una solicitud de obras por jornada asignada y temporada.';
    }
    if (code === 'INSUFFICIENT_CALENDAR') {
      return 'Calendario insuficiente: las obras no pueden cruzar de temporada.';
    }
    if (code === 'VERSION_CONFLICT') {
      return 'El estado o versión del estadio ha cambiado. Se ha actualizado la información.';
    }
    if (err?.status === 403) {
      return 'Acceso denegado: debes ser el mánager propietario del club.';
    }
    if (err?.status === 401) {
      return 'Sesión expirada. Por favor, vuelve a iniciar sesión.';
    }
    if (err?.status === 503) {
      return 'El servicio de remodelaciones no está disponible temporalmente (503).';
    }

    return message || 'Ha ocurrido un error al procesar la operación de remodelación.';
  }

  // --- STAFF & SIMULACIÓN ---
  getStaffRoleName(staffType: string): string {
    switch (staffType) {
      case 'GOALKEEPER_COACH':
        return this.game.t('stadium.staff_gk');
      case 'PHYSICAL_COACH':
        return this.game.t('stadium.staff_fitness');
      case 'SHOOTING_COACH':
        return this.game.t('stadium.staff_shooting');
      case 'TECHNICAL_COACH':
        return this.game.t('stadium.staff_tech');
      default:
        return staffType.replace(/_/g, ' ');
    }
  }

  getStaffLineBadge(line: string): string {
    switch (line) {
      case 'GOALKEEPER':
        return 'Portería';
      case 'DEFENCE':
        return 'Defensa';
      case 'FORWARD':
        return 'Delantera';
      case 'MIDFIELD':
        return 'Control';
      default:
        return line;
    }
  }

  getStaffAreaFromLine(line: string): 'goalkeeping' | 'fitness' | 'technical' | 'shooting' {
    switch (line) {
      case 'GOALKEEPER':
        return 'goalkeeping';
      case 'DEFENCE':
        return 'fitness';
      case 'MIDFIELD':
        return 'technical';
      case 'FORWARD':
      default:
        return 'shooting';
    }
  }

  attendanceSim() {
    const stadium = this.game.stadiumData();
    const fallback = this.game.userClub().stadium.modules;
    return estimateStadiumAttendance(this.effectiveCapacity, this.ticketPriceInput, {
      parkingLevel: stadium?.parkingLevel ?? fallback.parkingLevel,
      cafeteriaLevel: stadium?.cafeteriaLevel ?? fallback.cafeteriaLevel,
      merchandisingLevel: stadium?.merchandisingLevel ?? fallback.merchandisingLevel,
      bathroomLevel: stadium?.bathroomLevel ?? fallback.bathroomLevel,
    });
  }

  async applyTicketPrice() {
    this.isUpdatingTicketPrice.set(true);
    const parsedPrice = normalizeTicketPrice(this.ticketPriceInput);
    this.ticketPriceInput = parsedPrice;
    const currentPriceInfo = this.game.ticketPriceData();
    const expectedVersion = currentPriceInfo?.version;
    try {
      const res = await this.game.updateTicketPricing(parsedPrice, expectedVersion);
      if (res && typeof res.ticketPrice === 'number') {
        this.ticketPriceInput = res.ticketPrice;
      }
    } catch {
      // Si falló por conflicto, refrescar el input con el precio real del backend
      await this.game.fetchClubTicketPrice(this.game.userClub().id, true);
      const refreshed = this.game.ticketPriceData();
      if (refreshed && typeof refreshed.ticketPrice === 'number') {
        this.ticketPriceInput = refreshed.ticketPrice;
      }
    } finally {
      this.isUpdatingTicketPrice.set(false);
    }
  }

  applyLoan() {
    this.game.requestBankLoan(this.loanAmount, this.loanYears);
  }

  getConceptLabel(concept?: string, category?: string, direction?: string): string {
    if (!concept && !category) return 'Movimiento de Tesorería';

    const cUpper = (concept || '').toUpperCase().trim();
    const catUpper = (category || '').toUpperCase().trim();
    const isCredit = direction === 'CREDIT' || direction === 'INGRESO';

    if (isSportingAward(catUpper)) return 'Premios deportivos';

    if (cUpper.includes('CAPTURED TRANSFER') || cUpper.includes('CAPTURE TRANSFER')) {
      return 'Fichaje de Jugador';
    }
    if (cUpper.includes('INITIAL CLUB BUDGET') || cUpper.includes('INITIAL BUDGET')) {
      return 'Presupuesto Inicial';
    }
    if (cUpper.includes('BUYOUT') || cUpper.includes('RELEASE CLAUSE') || cUpper.includes('CLÁUSULA') || cUpper.includes('CLAUSULA')) {
      return isCredit ? 'Cobro por Cláusula de Rescisión' : 'Pago de Cláusula de Rescisión';
    }
    if (cUpper.includes('TICKET') || cUpper.includes('TAQUILLA') || cUpper.includes('MATCHDAY REVENUE')) {
      return 'Recaudación por Entradas y Taquilla';
    }
    if (cUpper.includes('SEASON TICKET') || cUpper.includes('ABONO') || cUpper.includes('ABONADO')) {
      return 'Ingresos por Campaña de Abonos';
    }
    if (cUpper.includes('STAFF') || cUpper.includes('SALARY') || cUpper.includes('SALARIES') || cUpper.includes('NÓMINA') || cUpper.includes('NOMINA')) {
      return 'Pago de Sueldos';
    }
    if (cUpper.includes('MATCHDAY LOAN INTEREST') || cUpper.includes('MATCH DAY LOAN INTEREST')) {
      return 'Intereses del Crédito';
    }
    if (catUpper === 'LOAN_INTEREST' || cUpper.includes('LOAN INTEREST')) {
      return 'Intereses de Préstamo Bancario';
    }
    if (cUpper.includes('LOAN DISBURSEMENT') || cUpper.includes('LOAN GRANTED')) {
      return 'Concesión de Préstamo Bancario';
    }
    if (cUpper.includes('LOAN REPAYMENT') || cUpper.includes('LOAN INSTALLMENT') || cUpper.includes('AMORTIZACIÓN') || cUpper.includes('AMORTIZACION')) {
      return 'Amortización de Préstamo Bancario';
    }
    if (cUpper.includes('SUBSCRIPTION') || cUpper.includes('MONTHLY FEE') || cUpper.includes('FEE')) {
      return 'Cuota de Mantenimiento de Competición';
    }
    if (cUpper.includes('STADIUM UPGRADE') || cUpper.includes('CAPACITY UPGRADE') || cUpper.includes('EXPANSION')) {
      return 'Inversión en Ampliación del Estadio';
    }
    if (cUpper.includes('STADIUM_RENAME')) {
      return 'Renombrar estadio';
    }
    if (cUpper.includes('COMMERCIAL') || cUpper.includes('MODULE')) {
      return 'Mejora de Módulos Comerciales';
    }
    if (cUpper.includes('PRIZE') || cUpper.includes('PREMIO')) {
      return 'Premio por Rendimiento Deportivo';
    }
    if (cUpper.includes('SPONSOR') || cUpper.includes('PATROCINIO')) {
      return 'Contrato de Patrocinio';
    }
    if (cUpper.includes('FACILITIES') || cUpper.includes('MANTENIMIENTO')) {
      return 'Mantenimiento de Instalaciones Deportivas';
    }
    
    if (cUpper.includes('COACH_SIGNING_BONUS') || cUpper.includes('PRIMA ENTRENADOR') || cUpper.includes('COACH SIGNING')) {
      return 'Prima de Fichaje de Entrenador';
    }
    if (cUpper.includes('COACH_TERMINATION') || cUpper.includes('INDEMNIZACIÓN ENTRENADOR') || cUpper.includes('INDEMNIZACION ENTRENADOR') || cUpper.includes('COACH TERMINATION')) {
      return 'Indemnización por Despido de Entrenador';
    }
    if (cUpper.includes('COACH_SALARY') || cUpper.includes('SUELDO ENTRENADOR') || cUpper.includes('SALARIO ENTRENADOR')) {
      return 'Salario de Entrenador';
    }

    if (catUpper === 'TRANSFER_PURCHASE') return 'Fichaje de Jugador en Mercado';
    if (catUpper === 'COACH_SIGNING_BONUS') return 'Prima de Fichaje de Entrenador';
    if (catUpper === 'COACH_TERMINATION') return 'Indemnización por Despido de Entrenador';
    if (catUpper === 'COACH_SALARY') return 'Salario de Entrenador';
    if (catUpper === 'INITIAL_BUDGET') return 'Presupuesto Inicial de Creación del Club';
    if (catUpper === 'TRANSFER_SALE') return 'Ingreso por Venta de Jugador';
    if (catUpper === 'RELEASE_CLAUSE' || catUpper === 'BUYOUT_CLAUSE') return isCredit ? 'Cobro de Cláusula de Rescisión' : 'Pago de Cláusula de Rescisión';
    if (catUpper === 'TICKET_SALES' || catUpper === 'TICKETING') return 'Recaudación de Taquilla y Entradas';
    if (catUpper === 'SEASON_TICKETS') return 'Ingresos por Abonos de Temporada';
    if (catUpper === 'STAFF_SALARY' || catUpper === 'SALARIES') return 'Pago de Fichas y Sueldos del Staff';
    if (catUpper === 'STADIUM_UPGRADE') return 'Obras y Mejoras en el Estadio';
    if (catUpper === 'MATCH_MERCHANDISING_INCOME') return 'Venta de Merchandising';
    if (catUpper === 'MATCH_CAFETERIA_INCOME') return 'Cafetería y Restaurantes';
    if (catUpper === 'MATCH_PARKING_INCOME') return 'Parking y Aparcamientos';
    if (catUpper === 'COMMERCIAL_MODULES' || catUpper === 'COMMERCIAL_REVENUE') return 'Ingresos por Actividad Comercial';
    if (catUpper === 'LOAN_DISBURSEMENT') return 'Ingreso por Préstamo Bancario';
    if (catUpper === 'LOAN_REPAYMENT') return 'Cuota de Préstamo Bancario';
    if (catUpper === 'SUBSCRIPTION_FEE') return 'Cuota de Inscripción / Mantenimiento';
    if (catUpper === 'PRIZE_MONEY') return 'Premio por Clasificación en Jornada';
    if (catUpper === 'SPONSORSHIP') return 'Ingresos por Patrocinio';
    if (catUpper === 'FACILITIES') return 'Mantenimiento de Instalaciones Deportivas';
    if (catUpper === 'LOAN_PRINCIPAL_REPAYMENT') return 'Amortización de Préstamo Bancario';
    if (catUpper === 'STADIUM_RENAME') return 'Renombrar estadio';

    return concept || 'Movimiento contable';
  }

  getCategoryLabel(category?: string): string {
    if (!category) return 'General';
    const catUpper = category.toUpperCase().trim();

    switch (catUpper) {
      case 'COACH_SIGNING_BONUS':
        return 'Prima Entrenador';
      case 'COACH_TERMINATION':
        return 'Indemnización Entrenador';
      case 'COACH_SALARY':
        return 'Salario Entrenador';
      case 'TRANSFER_PURCHASE':
        return 'Fichajes';
      case 'INITIAL_BUDGET':
        return 'Presupuesto Inicial';
      case 'TRANSFER_SALE':
        return 'Venta de Jugadores';
      case 'RELEASE_CLAUSE':
      case 'BUYOUT_CLAUSE':
        return 'Cláusula de Rescisión';
      case 'TICKET_SALES':
      case 'TICKETING':
        return 'Taquilla';
      case 'SEASON_TICKETS':
        return 'Abonos';
      case 'STAFF_SALARY':
      case 'SALARIES':
        return 'Sueldos y Fichas';
      case 'STADIUM_UPGRADE':
        return 'Infraestructura';
      case 'COMMERCIAL_MODULES':
      case 'COMMERCIAL_REVENUE':
        return 'Comercial';
      case 'LOAN_INTEREST':
        return 'Intereses';
      case 'LOAN_DISBURSEMENT':
        return 'Préstamo';
      case 'LOAN_REPAYMENT':
        return 'Amortización';
      case 'SUBSCRIPTION_FEE':
        return 'Suscripción';
      case 'PRIZE_MONEY':
      case 'MATCHDAY_AWARD':
        return 'Premios';
      case 'SPONSORSHIP':
        return 'Patrocinio';
      case 'FACILITIES':
        return 'Instalaciones';
      case 'MATCH_EXPENSE':
        return 'Gastos de Partido';
      default:
        return category
          .toLowerCase()
          .split('_')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');
    }
  }

  getSourceTypeLabel(sourceType?: string): string {
    if (!sourceType) return 'Club';
    const sUpper = sourceType.toUpperCase().trim();

    switch (sUpper) {
      case 'RESERVATION':
        return 'Mercado de Fichajes';
      case 'CLUB':
        return 'Administración';
      case 'STADIUM':
        return 'Estadio';
      case 'MATCH':
      case 'MATCHDAY':
        return 'Día de Partido';
      case 'PAYROLL':
        return 'Nóminas';
      case 'MAINTENANCE':
        return 'Mantenimiento';
      case 'CONTRACT':
        return 'Contrato';
      case 'LOAN':
        return 'Banca';
      case 'MARKET':
        return 'Mercado';
      case 'LEAGUE':
        return 'Competición';
      case 'MATCHDAY_LOAN':
        return 'Préstamo';
      case 'PLAYER_SALARY_DAY':
        return 'Sueldos de Jugadores';
      case 'MATCHDAY_AWARD':
        return 'Premios deportivos';
      case 'SPONSOR_CONTRACT':
        return 'Patrocinio';
      case 'FIXTURE':
        return 'Jornada';
      case 'STADIUM_UPGRADE':
        return 'Obras y Remodelación';
      default:
        return sourceType
          .toLowerCase()
          .split('_')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');
    }
  }

  // ==========================================
  // SPONSORS (PATROCINIOS DEL CLUB) UI LOGIC
  // ==========================================

  readonly selectedOfferForContract = signal<SponsorMarketOffer | null>(null);
  readonly isSubmittingSponsorContract = signal<boolean>(false);
  readonly contractError = signal<string | null>(null);
  readonly currentSponsorRequestId = signal<string | null>(null);
  readonly sponsorTypes: SponsorType[] = ['BILLBOARD', 'SHIRT', 'STADIUM'];

  getOffersForType(type: SponsorType): SponsorMarketOffer[] {
    return this.game.sponsorOffers().filter((o) => o.type === type);
  }

  isSponsorTypeOccupied(type: SponsorType): boolean {
    return this.game.occupiedSponsorTypes().has(type);
  }

  getOfferDisabledReason(offer: SponsorMarketOffer): string | null {
    if (offer.blockedReason) {
      return offer.blockedReason;
    }
    if (this.isSponsorTypeOccupied(offer.type)) {
      return 'Tipo de patrocinio ya ocupado';
    }
    return null;
  }

  openSponsorModal(offer: SponsorMarketOffer): void {
    this.selectedOfferForContract.set(offer);
    this.contractError.set(null);
    this.currentSponsorRequestId.set(crypto.randomUUID());
  }

  closeSponsorModal(): void {
    if (this.isSubmittingSponsorContract()) return;
    this.selectedOfferForContract.set(null);
    this.contractError.set(null);
    this.currentSponsorRequestId.set(null);
  }

  async confirmSponsorContract(): Promise<void> {
    const offer = this.selectedOfferForContract();
    if (!offer) return;
    const clubId = this.game.userClub()?.id || 'club-user';
    const reqId = this.currentSponsorRequestId() || crypto.randomUUID();
    this.currentSponsorRequestId.set(reqId);

    this.isSubmittingSponsorContract.set(true);
    this.contractError.set(null);

    try {
      await this.game.contractSponsorOffer(clubId, offer.id, reqId);
      this.selectedOfferForContract.set(null);
      this.currentSponsorRequestId.set(null);
    } catch (err: any) {
      this.contractError.set(err?.message || 'Error al procesar la contratación.');
    } finally {
      this.isSubmittingSponsorContract.set(false);
    }
  }

  getSponsorTypeLabel(type: SponsorType): string {
    switch (type) {
      case 'BILLBOARD': return 'Vallas Publicitarias';
      case 'SHIRT': return 'Camiseta Oficial';
      case 'STADIUM': return 'Naming del Estadio';
      default: return type;
    }
  }

  getSponsorTypeIcon(type: SponsorType): string {
    switch (type) {
      case 'BILLBOARD': return 'tv';
      case 'SHIRT': return 'award';
      case 'STADIUM': return 'building';
      default: return 'award';
    }
  }

  getSponsorTypeBadgeClass(type: SponsorType): string {
    switch (type) {
      case 'BILLBOARD':
        return 'bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800';
      case 'SHIRT':
        return 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
      case 'STADIUM':
        return 'bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800';
      default:
        return 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700';
    }
  }

  getSponsorContractStatusBadge(status?: string): { label: string; class: string } {
    switch (status) {
      case 'ACTIVE':
        return {
          label: 'Activo',
          class: 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950 dark:text-emerald-400 dark:border-emerald-800',
        };
      case 'PENDING_ACTIVATION':
        return {
          label: 'Pendiente de activación',
          class: 'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-800',
        };
      case 'EXPIRED':
        return {
          label: 'Caducado',
          class: 'bg-slate-100 text-slate-500 border border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700',
        };
      default:
        return {
          label: status || 'Estado desconocido',
          class: 'bg-slate-100 text-slate-600 border border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700',
        };
    }
  }

  getOfferFixedAmount(offer: SponsorMarketOffer): number {
    return offer.quote?.fixedAmount ?? offer.fixedAmount ?? 0;
  }

  getOfferWinBonusAmount(offer: SponsorMarketOffer): number | null {
    return offer.quote?.winBonusAmount ?? offer.winBonusAmount ?? null;
  }

  getOfferDuration(offer: SponsorMarketOffer): number {
    return offer.durationMatchdays ?? offer.quote?.durationMatchdays ?? 0;
  }

  getContractFixedAmount(contract: SponsorContract): number {
    return contract.terms?.fixedAmount ?? contract.fixedAmount ?? 0;
  }

  getContractWinBonusAmount(contract: SponsorContract): number | null {
    return contract.terms?.winBonusAmount ?? contract.winBonusAmount ?? null;
  }

  formatAmount(val?: number | null): string {
    if (val === undefined || val === null) return '0 €';
    const num = Number(val);
    if (isNaN(num)) return '0 €';
    if (num >= 1_000_000) {
      return `${(num / 1_000_000).toFixed(2)} M€`;
    }
    const hasDecimals = num % 1 !== 0;
    return `${num.toLocaleString('es-ES', {
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: 2,
    })} €`;
  }
}
